import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, TablePaginationConfig, Modal, message, Input, Space, Button, notification, Tooltip } from "antd";
import { SorterResult } from "antd/lib/table/interface";
import axios from "axios";
import { SearchOutlined } from "@ant-design/icons";
import useSWR from "swr"
import { useAuth } from "../../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

const RemarkModalContent: React.FC<{ onChange: (val: string) => void }> = ({ onChange }) => {
    const [val, setVal] = useState("Supply Slip Return ");

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        let value = e.target.value;
        if (!value.startsWith("Supply Slip Return")) {
            value = "Supply Slip Return";
        }
        setVal(value);
        onChange(value);
    };

    return (
        <Input.TextArea
            rows={3}
            value={val}
            onChange={handleChange}
            style={{ width: "100%", marginTop: "10px" }}
        />
    );
};

export interface SupplySlipReturnProps {
    form: any;
    status: string;
    supplySlipNo: string;
    supplySlipDate: string;
    workOrderNo: string;
    workOrderDate: string;
    noPolisi: string;
    warehouseNameDisplay: string;
    autosaveStatus: string;
    isDataSupplySlipModalVisible: boolean;
    isAddItemModalVisible: boolean;
    handleOpenDataSupplySlip: () => void;
    handleCloseDataSupplySlip: () => void;
    handleCloseAddItem: () => void;
    handleSelectSupplySlip: (record: any) => void;
    tableData: any[];
    columns: any[];
    pagination: TablePaginationConfig;
    rowSelection: {
        selectedRowKeys: React.Key[];
        onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => void;
    };
    loading: boolean;
    handleNew: () => void;
    handleSubmit: () => void;
    handleVoid: () => void;
    handlePrint: () => void;
    handleSearchWorkOrder: () => void;
    handleAdd: () => void;
    handleAddItemToTable: (selectedItems: any[]) => void;
    handleDelete: () => void;
    onTableChange: (
        pagination: TablePaginationConfig,
        filters: Record<string, any>,
        sorter: SorterResult<any> | SorterResult<any>[]
    ) => void;
    selectedRowKeys: React.Key[];
    lockedFields: Record<string, { userName: string; socketId: string }>;
    onFieldFocus: (fieldName: string) => void;
    onFieldBlur: (fieldName: string) => void;
    onFormValuesChange: (changedValues: any, allValues: any) => void;
    getLockProps: (fieldName: string) => any;
}

const withSupplySlipReturnLogic = <P extends object>(
    WrappedComponent: React.ComponentType<P & SupplySlipReturnProps>
) => {
    const HOC: React.FC<P> = (props) => {
        const [form] = Form.useForm();
        const { socket, userData } = useAuth();
        const [lockedFields, setLockedFields] = useState<Record<string, { userName: string; socketId: string }>>({});
        const lockedFieldsRef = useRef(lockedFields);
        lockedFieldsRef.current = lockedFields;
        const location = useLocation();
        const navigate = useNavigate();
        const fetcher = (url: string) => axios.get(url).then((res) => res.data);
        const { data: warehouseData } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/warehouse/list`, fetcher);
        const getColumnSearchProps = (dataIndex: string) => ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder={`Search ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                        <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                        <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Reset</Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
            onFilter: (value: any, record: any) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        });
        const [supplySlipReturnNo, setSupplySlipReturnNo] = useState<string>("");
        const [supplySlipReturnDate, setSupplySlipReturnDate] = useState<string>("");
        const [status, setStatus] = useState<string>("Draft");
        const [supplySlipNo, setSupplySlipNo] = useState<string>("");
        const [supplySlipDate, setSupplySlipDate] = useState<string>("");
        const [workOrderNo, setWorkOrderNo] = useState<string>("");
        const [workOrderDate, setWorkOrderDate] = useState<string>("");
        const [noPolisi, setNoPolisi] = useState<string>("");
        const [warehouseNameDisplay, setWarehouseNameDisplay] = useState<string>("");
        const [isDataSupplySlipModalVisible, setIsDataSupplySlipModalVisible] = useState<boolean>(false);
        const [isAddItemModalVisible, setIsAddItemModalVisible] = useState<boolean>(false);
        const [autosaveStatus, setAutosaveStatus] = useState<string>("idle");
        const [tableData, setTableData] = useState<any[]>([]);
        const [loading, setLoading] = useState<boolean>(false);
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const [pagination, setPagination] = useState<TablePaginationConfig>({
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} dari ${total} item`,
        });
        const currentSupplySlipReturnNo = Form.useWatch('supply_slip_return_no', form) || supplySlipReturnNo;
        useEffect(() => {
            if (!socket || !currentSupplySlipReturnNo) return;
            socket.emit("join_supply_slip_room", { supplySlipNo: currentSupplySlipReturnNo });
            const handleCurrentLocks = (locks: Record<string, { userName: string; socketId: string }>) => setLockedFields(locks);
            const handleFieldLocked = (data: { fieldName: string; userName: string; socketId: string }) => {
                setLockedFields((prev) => ({ ...prev, [data.fieldName]: { userName: data.userName, socketId: data.socketId } }));
            };
            const handleFieldUnlocked = (data: { fieldName: string }) => {
                setLockedFields((prev) => {
                    const updated = { ...prev };
                    delete updated[data.fieldName];
                    return updated;
                });
            };
            const handleReceiveSyncData = (data: { changedValues?: any; allValues?: any; tableItems?: any[] }) => {
                if (data.changedValues) form.setFieldsValue(data.changedValues);
                if (data.allValues) form.setFieldsValue(data.allValues);
                if (data.tableItems) setTableData(data.tableItems);
            };
            const handleRecordVoided = (data: { supplySlipNo: string }) => {
                if (data.supplySlipNo === currentSupplySlipReturnNo) {
                    notification.warning({ message: "Pemberitahuan", description: "Dokumen ini telah di-void oleh pengguna lain." });
                    handleNew();
                }
            };
            const handleRecordSubmitted = (data: { supplySlipNo: string; status?: string }) => {
                if (data.supplySlipNo === currentSupplySlipReturnNo) {
                    notification.info({ message: "Pemberitahuan", description: "Dokumen ini telah di-submit oleh pengguna lain." });
                    const nextStatus = data.status || "Complete";
                    setStatus(nextStatus);
                    form.setFieldsValue({ status: nextStatus });
                    setAutosaveStatus("stopped");
                    setLockedFields({});
                }
            };
            socket.on("supply_slip_current_locks", handleCurrentLocks);
            socket.on("supply_slip_field_locked", handleFieldLocked);
            socket.on("supply_slip_field_unlocked", handleFieldUnlocked);
            socket.on("supply_slip_receive_sync_data", handleReceiveSyncData);
            socket.on("supply_slip_record_voided", handleRecordVoided);
            socket.on("supply_slip_record_submitted", handleRecordSubmitted);
            return () => {
                socket.emit("leave_supply_slip_room", { supplySlipNo: currentSupplySlipReturnNo });
                socket.off("supply_slip_current_locks", handleCurrentLocks);
                socket.off("supply_slip_field_locked", handleFieldLocked);
                socket.off("supply_slip_field_unlocked", handleFieldUnlocked);
                socket.off("supply_slip_receive_sync_data", handleReceiveSyncData);
                socket.off("supply_slip_record_voided", handleRecordVoided);
                socket.off("supply_slip_record_submitted", handleRecordSubmitted);
            };
        }, [socket, currentSupplySlipReturnNo, form]);

        const onFieldFocus = useCallback((fieldName: string) => {
            if (socket && currentSupplySlipReturnNo) {
                socket.emit("supply_slip_lock_field", {
                    supplySlipNo: currentSupplySlipReturnNo,
                    fieldName,
                    userName: userData?.nama || "User Lain"
                });
            }
        }, [socket, currentSupplySlipReturnNo, userData?.nama]);
        const onFieldBlur = useCallback((fieldName: string) => {
            if (socket && currentSupplySlipReturnNo) {
                socket.emit("supply_slip_unlock_field", {
                    supplySlipNo: currentSupplySlipReturnNo,
                    fieldName
                });
            }
        }, [socket, currentSupplySlipReturnNo]);
        const onFormValuesChange = useCallback((changedValues: any, allValues: any) => {
            if (socket && currentSupplySlipReturnNo) {
                socket.emit("supply_slip_sync_field_data", {
                    supplySlipNo: currentSupplySlipReturnNo,
                    changedValues,
                    allValues,
                    tableItems: tableData
                });
            }
        }, [socket, currentSupplySlipReturnNo, tableData]);
        const getLockProps = useCallback((fieldName: string) => ({
            help: lockedFields?.[fieldName] ? `Sedang diedit oleh ${lockedFields[fieldName].userName}` : undefined,
            validateStatus: (lockedFields?.[fieldName] ? "warning" : undefined) as "warning" | undefined,
        }), [lockedFields]);

        useEffect(() => {
            form.setFieldsValue({
                status: status,
                supply_slip_return_no: supplySlipReturnNo,
                supply_slip_return_date: supplySlipReturnDate,
                supply_slip_no: supplySlipNo,
                supply_slip_date: supplySlipDate,
                work_order_no: workOrderNo,
                work_order_date: workOrderDate,
                no_polisi: noPolisi,
                warehouse_name_display: warehouseNameDisplay,
            });
        }, [form, status, supplySlipReturnNo, supplySlipReturnDate, supplySlipNo, supplySlipDate, workOrderNo, workOrderDate, noPolisi, warehouseNameDisplay]);

        useEffect(() => {
            const fetchInitialDate = async () => {
                try {
                    const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/generate-info`);
                    setSupplySlipReturnDate(res.data.returnDate);
                } catch (e) { console.error(e); }
            };
            fetchInitialDate();
        }, []);

        const columns = [
            {
                title: 'Item Code',
                dataIndex: 'item_code',
                key: 'item_code',
                width: 120,
                ...getColumnSearchProps('item_code'),
            },
            {
                title: 'Item Name',
                dataIndex: 'item_name',
                key: 'item_name',
                width: 200,
                ...getColumnSearchProps('item_name'),
            },
            {
                title: 'Quantity',
                dataIndex: 'qty',
                key: 'qty',
                width: 100,
            },
            {
                title: 'Qty Return',
                dataIndex: 'qty_return',
                key: 'qty_return',
                width: 120,
                render: (text: string, record: any) => {
                    const isExceed = Number(record.qty_return ?? text) > Number(record.qty);
                    const lockInfo = lockedFieldsRef.current?.[`qty_return_${record.key}`];
                    return (
                        <Tooltip title={lockInfo ? `Sedang diedit oleh ${lockInfo.userName}` : undefined}>
                            <div>
                                <Input
                                    status={isExceed ? 'error' : (lockInfo ? 'warning' : '')}
                                    disabled={!!lockInfo}
                                    value={record.qty_return ?? text}
                                    onFocus={() => onFieldFocus(`qty_return_${record.key}`)}
                                    onBlur={() => onFieldBlur(`qty_return_${record.key}`)}
                                    onChange={(e) => handleUpdateQtyReturn(record.key, e.target.value)}
                                />
                                {isExceed && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>Exceed Qty</div>}
                            </div>
                        </Tooltip>
                    );
                }
            },
            {
                title: 'Location',
                dataIndex: 'location',
                key: 'location',
                width: 150,
                ...getColumnSearchProps('location'),
            },
            {
                title: 'Remarks',
                dataIndex: 'remarks',
                key: 'remarks',
                width: 200,
                render: (text: string, record: any) => {
                    const lockInfo = lockedFieldsRef.current?.[`remarks_${record.key}`];
                    return (
                        <Tooltip title={lockInfo ? `Sedang diedit oleh ${lockInfo.userName}` : undefined}>
                            <div>
                                <Input
                                    status={lockInfo ? 'warning' : ''}
                                    disabled={!!lockInfo}
                                    value={record.remarks ?? text}
                                    onFocus={() => onFieldFocus(`remarks_${record.key}`)}
                                    onBlur={() => onFieldBlur(`remarks_${record.key}`)}
                                    onChange={(e) => handleUpdateRemark(record.key, e.target.value)}
                                />
                            </div>
                        </Tooltip>
                    );
                },
            },
        ];

        const formatStatus = (val: any) => {
            if (String(val) === '2') return 'Waiting for Approved';
            if (String(val) === '1') return 'Draft';
            if (String(val) === '4') return 'Complete';
            if (String(val) === '5') return 'Canceled';
            return val;
        }

        const handleUpdateRemark = (key: React.Key, value: string) => {
            setTableData(prev => {
                const newTable = prev.map(item => item.key === key ? { ...item, remarks: value } : item);
                if (socket && currentSupplySlipReturnNo) {
                    socket.emit("supply_slip_sync_field_data", {
                        supplySlipNo: currentSupplySlipReturnNo,
                        allValues: form.getFieldsValue(true),
                        tableItems: newTable
                    });
                }
                return newTable;
            });
        };

        const handleUpdateQtyReturn = (key: React.Key, value: string) => {
            setTableData(prev => {
                const newTable = prev.map(item => item.key === key ? { ...item, qty_return: value } : item);
                if (socket && currentSupplySlipReturnNo) {
                    socket.emit("supply_slip_sync_field_data", {
                        supplySlipNo: currentSupplySlipReturnNo,
                        allValues: form.getFieldsValue(true),
                        tableItems: newTable
                    });
                }
                return newTable;
            });
        };

        const performAutosave = useCallback(async () => {
            const values = form.getFieldsValue();
            if (!values.work_order_no) return;
            if (values.status !== "Draft" && values.status !== "1") {
                setAutosaveStatus("stopped");
                return;
            }
            setAutosaveStatus("processing");
            try {
                await new Promise(resolve => setTimeout(resolve, 300));
                const payload = {
                    header: {
                        ...values,
                        supply_slip_return_no: values.supply_slip_return_no,
                        supply_slip_return_date: values.supply_slip_return_date,
                        status: values.status === "Draft" ? 1 : values.status,
                    },
                    items: [
                        ...tableData.map(item => ({
                            supply_slip_return_no: values.supply_slip_return_no,
                            item_code: item.item_code,
                            item_name: item.item_name,
                            qty: item.qty,
                            qty_return: item.qty_return,
                            location: item.location,
                            remarks: item.remarks,
                            type: "1"
                        })),
                    ]
                };
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/save`, payload);
                setAutosaveStatus("idle");
            } catch (error) {
                setAutosaveStatus("error");
                console.error("Autosave failed:", error);
            }
        }, [form, tableData]);

        useEffect(() => {
            const delayDebounceFn = setTimeout(() => {
                const currentStatus = form.getFieldValue('status');
                if (form.getFieldValue('work_order_no') && (currentStatus === "Draft" || currentStatus === "1")) {
                    performAutosave();
                } else if (form.getFieldValue('work_order_no')) {
                    setAutosaveStatus("stopped");
                }
            }, 1000);
            return () => clearTimeout(delayDebounceFn);
        }, [tableData, performAutosave, form]);

        const handleSelectSupplySlip = useCallback(async (record: any) => {
            const selectedWarehouse = warehouseData?.find((w: any) => w.warehouse_code === record.warehouse);
            const warehouseLabel = selectedWarehouse ? selectedWarehouse.warehouse_name : record.warehouse;
            try {
                if (!record.supply_slip_return_no) {
                    const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/generate-info`);
                    setSupplySlipReturnNo(res.data.returnNo);
                } else {
                    setSupplySlipReturnNo(record.supply_slip_return_no);
                }
            } catch (e) { message.error("Gagal generate nomor return"); }
            setWorkOrderNo(record.work_order_no);
            setWorkOrderDate(record.work_order_date);
            setNoPolisi(record.no_polisi);
            setWarehouseNameDisplay(warehouseLabel);
            const displayStatus = formatStatus(record.status || "Draft");
            setStatus(displayStatus);
            form.setFieldsValue({
                work_order_no: record.work_order_no,
                work_order_date: record.work_order_date,
                no_polisi: record.no_polisi,
                warehouse: record.warehouse,
                warehouse_name_display: warehouseLabel,
            });

            try {
                if (record.items) {
                    setTableData(record.items.map((item: any, index: number) => ({ ...item, key: index + 1 })));
                } else {
                    const endpoint = record.supply_slip_return_no
                        ? `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/items-return/${record.supply_slip_return_no}`
                        : `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/items/${record.work_order_no}`;
                    const response = await axios.get(endpoint);
                    if (record.supply_slip_return_no) {
                        const spareparts = response.data.filter((item: any) => String(item.type) === '1');
                        setTableData(spareparts.map((item: any, index: number) => ({ ...item, key: index + 1, supply_slip_no: item.supply_slip_no })));
                    } else {
                        let itemsWithKeys = response.data.map((item: any, index: number) => {
                            const availableQty = Number(item.qty || 0);
                            return {
                                ...item,
                                key: index + 1,
                                qty: availableQty
                            };
                        }).filter((item: any) => item.qty > 0);
                        if (record.supply_slip_return_no && (displayStatus === "Draft" || displayStatus === "1")) {
                            const sourceRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/items/${record.supply_slip_no}`);
                            itemsWithKeys = itemsWithKeys.map((item: any) => {
                                const sourceItem = sourceRes.data.find((si: any) => si.item_code === item.item_code);
                                return sourceItem ? { ...item, qty: sourceItem.qty } : item;
                            });
                        }
                        setTableData(itemsWithKeys);
                    }
                }
            } catch (error) {
                message.error("Gagal mengambil data item detail.");
            }
            setIsDataSupplySlipModalVisible(false);
        }, [form, warehouseData]);

        useEffect(() => {
            if (location.state?.record) {
                const { record } = location.state;
                handleSelectSupplySlip(record);
                if (record.supply_slip_return_no) {
                    setSupplySlipReturnNo(record.supply_slip_return_no);
                    setStatus(formatStatus(record.status));
                }
            }
        }, [location.state, handleSelectSupplySlip]);

        const onSelectChange = useCallback((newSelectedRowKeys: React.Key[]) => {
            const addedKeys = newSelectedRowKeys.filter((k) => !selectedRowKeys.includes(k));
            const removedKeys = selectedRowKeys.filter((k) => !newSelectedRowKeys.includes(k));
            if (socket && currentSupplySlipReturnNo) {
                addedKeys.forEach((key) => {
                    socket.emit("supply_slip_lock_field", {
                        supplySlipNo: currentSupplySlipReturnNo,
                        fieldName: `row_${key}`,
                        userName: userData?.nama || "User Lain",
                    });
                });
                removedKeys.forEach((key) => {
                    socket.emit("supply_slip_unlock_field", {
                        supplySlipNo: currentSupplySlipReturnNo,
                        fieldName: `row_${key}`,
                    });
                });
            }
            setSelectedRowKeys(newSelectedRowKeys);
        }, [selectedRowKeys, socket, currentSupplySlipReturnNo, userData?.nama]);

        const rowSelection = {
            selectedRowKeys,
            onChange: onSelectChange,
        };


        const fetchData = useCallback(async (current: number, pageSize: number) => {
            setLoading(true);
            setTimeout(() => {
                setPagination(prev => ({ ...prev, total: 0, current, pageSize }));
                setLoading(false);
            }, 500);
        }, []);


        useEffect(() => {
            if (!supplySlipNo) {
                fetchData(pagination.current!, pagination.pageSize!);
            }
        }, [fetchData, pagination.current, pagination.pageSize, supplySlipNo]);

        const handleNew = useCallback(async () => {
            try {
                if (socket && currentSupplySlipReturnNo) {
                    socket.emit("leave_supply_slip_room", { supplySlipNo: currentSupplySlipReturnNo });
                }
                navigate(location.pathname, { replace: true, state: null });
                form.resetFields();
                let initialDate = "";
                try {
                    const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/generate-info`);
                    initialDate = res.data.returnDate;
                } catch (e) {
                    console.error(e);
                }
                setStatus("Draft");
                setSupplySlipReturnNo("");
                setSupplySlipReturnDate(initialDate);
                setSupplySlipNo("");
                setSupplySlipDate("");
                setWorkOrderNo("");
                setWorkOrderDate("");
                setNoPolisi("");
                setWarehouseNameDisplay("");
                setTableData([]);
                setSelectedRowKeys([]);
                setLockedFields({});
                setAutosaveStatus("idle");
                setIsDataSupplySlipModalVisible(false);
                setIsAddItemModalVisible(false);
                setPagination(prev => ({ ...prev, current: 1, total: 0 }));
                form.setFieldsValue({
                    status: "Draft",
                    supply_slip_return_no: "",
                    supply_slip_return_date: initialDate,
                    work_order_no: "",
                    work_order_date: "",
                    no_polisi: "",
                    warehouse: "",
                    warehouse_name_display: "",
                });
                notification.success({
                    message: 'Berhasil',
                    description: 'Form dan tabel berhasil dikosongkan.',
                });
            } catch (error) {
                notification.error({
                    message: 'Gagal',
                    description: 'Terjadi kesalahan saat mengosongkan form.',
                });
            }
        }, [form, socket, currentSupplySlipReturnNo, navigate, location.pathname]);

        useEffect(() => {
            const checkDocumentOptimisticLock = async () => {
                const returnNoValue = form.getFieldValue('supply_slip_return_no') || supplySlipReturnNo;
                const woValue = form.getFieldValue('work_order_no');
                if (!returnNoValue || !woValue) return;
                try {
                    const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/check-exists/${returnNoValue}`);
                    if (!res.data.exists) {
                        notification.warning({
                            message: "Optimistic Locking",
                            description: "Dokumen ini telah di-void oleh pengguna lain. Halaman di-reset.",
                        });
                        handleNew();
                    } else if (res.data.status === '4' || res.data.status === 4) {
                        if (status === "Draft" || status === "1") {
                            notification.info({
                                message: "Optimistic Locking",
                                description: "Dokumen ini telah di-submit oleh pengguna lain.",
                            });
                            setStatus("Complete");
                            form.setFieldsValue({ status: "Complete" });
                            setAutosaveStatus("stopped");
                        }
                    }
                } catch (err) {
                    console.error("Gagal verifikasi optimistic lock:", err);
                }
            };
            const intervalId = setInterval(checkDocumentOptimisticLock, 3000);
            return () => clearInterval(intervalId);
        }, [form, supplySlipReturnNo, status, handleNew]);

        const handleSubmit = useCallback(async () => {
            try {
                const values = await form.validateFields();
                if (tableData.length === 0) {
                    return notification.error({ message: 'Gagal', description: 'Tabel item tidak boleh kosong.' });
                }
                for (const item of tableData) {
                    if (!item.location || String(item.location).trim() === "") {
                        Modal.warning({
                            title: 'Peringatan Validasi',
                            content: 'Location Code Null !',
                        });
                        return;
                    }
                }
                for (const item of tableData) {
                    if (!item.qty_return || String(item.qty_return).trim() === "") {
                        notification.warning({
                            message: 'Peringatan Validasi',
                            description: `Qty Return untuk item ${item.item_code} belum diisi.`,
                        });
                        return;
                    }
                    if (Number(item.qty_return) > Number(item.qty)) {
                        notification.warning({
                            message: 'Peringatan Validasi',
                            description: 'Harap Periksa nilai berlebih "Qty Return"',
                        });
                        return;
                    }
                    if (!item.remarks || String(item.remarks).trim() === "") {
                        notification.warning({
                            message: 'Peringatan Validasi',
                            description: `Remarks untuk item ${item.item_code} belum diisi.`,
                        });
                        return;
                    }
                }

                Modal.confirm({
                    title: 'Confirm',
                    content: 'Are you sure return item ?',
                    okText: 'Yes',
                    cancelText: 'No',
                    onOk: async () => {
                        setLoading(true);
                        try {
                            const payload = {
                                header: { ...values, status: '4' },
                                items: [
                                    ...tableData.map(item => ({
                                        ...item,
                                        supply_slip_return_no: values.supply_slip_return_no,
                                        type: "1"
                                    })),
                                ]
                            };
                            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/submit`, payload);
                            if (socket && values.supply_slip_return_no) {
                                socket.emit("supply_slip_record_submitted", { supplySlipNo: values.supply_slip_return_no, status: "Complete" });
                            }
                            notification.success({ message: 'Berhasil', description: 'Data return berhasil diproses dan stok diperbarui.' });
                            setStatus("Complete");
                            form.setFieldsValue({ status: "Complete" });
                            setAutosaveStatus("stopped");
                        } catch (error) {
                            console.error("Gagal:", error);
                            setStatus("Draft");
                            form.setFieldsValue({ status: "Draft" });
                            notification.error({ message: 'Gagal', description: 'Gagal memproses submit. Status dikembalikan ke Draft.' });
                        } finally {
                            setLoading(false);
                        }
                    }
                });

            } catch (errorInfo) {
                console.log("Gagal:", errorInfo);
                notification.error({ message: 'Gagal', description: 'Harap isi semua field atau periksa koneksi.' });
            }
        }, [form, tableData, handleNew, userData]);

        const handleVoid = useCallback(() => {
            if (!supplySlipReturnNo) return;
            Modal.confirm({
                title: 'Konfirmasi Hapus Data',
                content: 'Apakah Anda yakin ingin menghapus data ini secara permanen dari database?',
                onOk: async () => {
                    try {
                        await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/supply-slip-return/void/${supplySlipReturnNo}`);
                        if (socket && supplySlipReturnNo) {
                            socket.emit("supply_slip_record_voided", { supplySlipNo: supplySlipReturnNo });
                        }
                        notification.success({ message: 'Berhasil', description: 'Data Supply Slip Return berhasil dihapus!' });
                        handleNew();
                    } catch (error) {
                        console.error("Void failed:", error);
                        notification.error({ message: 'Gagal', description: 'Gagal menghapus data. Silakan coba lagi.' });
                    }
                },
                onCancel() {
                    console.log('Void dibatalkan');
                },
            });
        }, [supplySlipReturnNo, handleNew]);

        const handlePrint = useCallback(() => {
            message.info("Tombol Print diklik!");

        }, []);

        const handleOpenDataSupplySlip = useCallback(() => {
            if (lockedFields?.["work_order_no"]) {
                notification.warning({
                    message: "Field Sedang Digunakan",
                    description: `Work order sedang dipilih oleh ${lockedFields["work_order_no"].userName}`
                });
                return;
            }
            onFieldFocus("work_order_no");
            setIsDataSupplySlipModalVisible(true);
        }, [lockedFields, onFieldFocus]);

        const handleCloseDataSupplySlip = useCallback(() => {
            onFieldBlur("work_order_no");
            setIsDataSupplySlipModalVisible(false);
        }, [onFieldBlur]);

        const handleCloseAddItem = useCallback(() => {
            onFieldBlur("add_item_modal");
            setIsAddItemModalVisible(false);
        }, [onFieldBlur]);

        const handleSearchWorkOrder = useCallback(() => {
            message.info("Tombol Cari Work Order diklik!");

            Modal.info({
                title: 'Pilih Work Order',
                content: 'Ini akan membuka modal untuk mencari dan memilih Work Order.',
                onOk() {
                    setWorkOrderNo("WO-20231026-001");
                    setWorkOrderDate("2023-10-26");
                    setNoPolisi("B 1234 ABC");
                    setWarehouseNameDisplay("Gudang Utama");
                    form.setFieldsValue({
                        work_order_no: "WO-20231026-001",
                        work_order_date: "2023-10-26",
                        no_polisi: "B 1234 ABC",
                        warehouse_name_display: "Gudang Utama",
                    });
                },
            });
        }, [form, fetchData, pagination.pageSize]);

        const handleAdd = useCallback(() => {
            if (lockedFields?.["add_item_modal"]) {
                notification.warning({
                    message: "Modal Sedang Digunakan",
                    description: `Modal Add Item sedang dibuka oleh ${lockedFields["add_item_modal"].userName}`
                });
                return;
            }
            onFieldFocus("add_item_modal");
            setIsAddItemModalVisible(true);
        }, [lockedFields, onFieldFocus]);

        const handleAddItemToTable = useCallback((selectedItems: any[]) => {
            const spareparts = selectedItems.filter(item => String(item.type) === '1');

            if (spareparts.length > 0) {
                setTableData(prev => {
                    const newTable = [
                        ...prev,
                        ...spareparts.map((item, index) => ({
                            ...item,
                            key: prev.length + index + 1,
                            qty_return: item.qty,
                            remarks: ''
                        }))
                    ];
                    if (socket && currentSupplySlipReturnNo) {
                        socket.emit("supply_slip_sync_field_data", {
                            supplySlipNo: currentSupplySlipReturnNo,
                            allValues: form.getFieldsValue(true),
                            tableItems: newTable
                        });
                    }
                    return newTable;
                });
            }
            onFieldBlur("add_item_modal");
            setIsAddItemModalVisible(false);
        }, [socket, currentSupplySlipReturnNo, form, onFieldBlur]);

        const handleDelete = useCallback(() => {
            const returnNo = form.getFieldValue('supply_slip_return_no');
            const keysToDelete = [...selectedRowKeys];
            if (socket && currentSupplySlipReturnNo) {
                keysToDelete.forEach((key) => {
                    socket.emit("supply_slip_unlock_field", {
                        supplySlipNo: currentSupplySlipReturnNo,
                        fieldName: `row_${key}`,
                    });
                });
            }
            const newData = tableData.filter(item => !selectedRowKeys.includes(item.key));
            setTableData(newData);
            if (socket && currentSupplySlipReturnNo) {
                socket.emit("supply_slip_sync_field_data", {
                    supplySlipNo: currentSupplySlipReturnNo,
                    allValues: form.getFieldsValue(true),
                    tableItems: newData,
                });
            }
            setSelectedRowKeys([]);
            if (returnNo && selectedRowKeys.length > 0) {
                setAutosaveStatus("processing");
            }
        }, [selectedRowKeys, tableData, form, socket, currentSupplySlipReturnNo]);

        const onTableChange = useCallback((
            newPagination: TablePaginationConfig,
            filters: Record<string, any>,
            sorter: SorterResult<any> | SorterResult<any>[]
        ) => {
            console.log('Tabel berubah:', newPagination, filters, sorter);
            setPagination(newPagination);
        }, []);

        return (
            <WrappedComponent
                {...(props as P)}
                form={form}
                status={status}
                supplySlipNo={supplySlipNo}
                supplySlipDate={supplySlipDate}
                workOrderNo={workOrderNo}
                workOrderDate={workOrderDate}
                noPolisi={noPolisi}
                warehouseNameDisplay={warehouseNameDisplay}
                isDataSupplySlipModalVisible={isDataSupplySlipModalVisible}
                handleOpenDataSupplySlip={handleOpenDataSupplySlip}
                handleCloseDataSupplySlip={handleCloseDataSupplySlip}
                tableData={tableData}
                columns={columns}
                pagination={pagination}
                rowSelection={rowSelection}
                loading={loading}
                handleNew={handleNew}
                handleSubmit={handleSubmit}
                handleVoid={handleVoid}
                handlePrint={handlePrint}
                handleSearchWorkOrder={handleSearchWorkOrder}
                handleAdd={handleAdd}
                handleAddItemToTable={handleAddItemToTable}
                isAddItemModalVisible={isAddItemModalVisible}
                handleCloseAddItem={handleCloseAddItem}
                handleDelete={handleDelete}
                handleSelectSupplySlip={handleSelectSupplySlip}
                onTableChange={onTableChange}
                autosaveStatus={autosaveStatus}
                selectedRowKeys={selectedRowKeys}
                lockedFields={lockedFields}
                onFieldFocus={onFieldFocus}
                onFieldBlur={onFieldBlur}
                onFormValuesChange={onFormValuesChange}
                getLockProps={getLockProps}
            />
        );
    };
    return HOC;
};

export default withSupplySlipReturnLogic;